import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import { Plus, Trash2, Calculator, ArrowRightLeft, Briefcase, Video, BookOpen, Activity, Truck, PiggyBank } from 'lucide-react';
import Navigation from '../components/Navigation';
import { getCurrentMonthYear } from '../utils/currentDate';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { formatNumber } from '../utils/timeCalculations';

const CalculadoraTempoPage = () => {
  const [mode, setMode] = useState('somar'); // 'somar', 'subtrair', 'converter'
  
  // For sum mode - multiple time entries
  const [timeEntries, setTimeEntries] = useState([
    { id: 1, hours: 0, minutes: 0, seconds: 0 },
    { id: 2, hours: 0, minutes: 0, seconds: 0 }
  ]);
  
  // For subtract mode
  const [time1, setTime1] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [time2, setTime2] = useState({ hours: 0, minutes: 0, seconds: 0 });
  
  // For convert mode
  const [convertValue, setConvertValue] = useState('');
  const [convertFrom, setConvertFrom] = useState('minutos');
  const [convertTo, setConvertTo] = useState('horas');

  // Calculate sum result
  const sumResult = useMemo(() => {
    if (mode !== 'somar') return null;
    
    let totalSeconds = 0;
    timeEntries.forEach(entry => {
      totalSeconds += (entry.hours * 3600) + (entry.minutes * 60) + entry.seconds;
    });
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const decimal = (totalSeconds / 3600).toFixed(2);
    
    return {
      formatted: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
      decimal,
      hours,
      minutes,
      seconds
    };
  }, [timeEntries, mode]);

  // Calculate subtract result
  const subtractResult = useMemo(() => {
    if (mode !== 'subtrair') return null;
    
    const seconds1 = (time1.hours * 3600) + (time1.minutes * 60) + time1.seconds;
    const seconds2 = (time2.hours * 3600) + (time2.minutes * 60) + time2.seconds;
    const diff = Math.abs(seconds1 - seconds2);
    
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    const decimal = (diff / 3600).toFixed(2);
    
    return {
      formatted: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
      decimal,
      hours,
      minutes,
      seconds
    };
  }, [time1, time2, mode]);

  // Calculate conversion result
  const convertResult = useMemo(() => {
    if (mode !== 'converter' || !convertValue) return null;
    
    const value = parseFloat(convertValue);
    if (isNaN(value)) return null;
    
    let seconds = 0;
    
    // Convert to seconds first
    switch(convertFrom) {
      case 'horas':
        seconds = value * 3600;
        break;
      case 'minutos':
        seconds = value * 60;
        break;
      case 'segundos':
        seconds = value;
        break;
      default:
        seconds = 0;
    }
    
    // Convert from seconds to target unit
    let result = 0;
    switch(convertTo) {
      case 'horas':
        result = seconds / 3600;
        break;
      case 'minutos':
        result = seconds / 60;
        break;
      case 'segundos':
        result = seconds;
        break;
      default:
        result = 0;
    }
    
    // Round to whole seconds first: 0.7 h = 2519.9999… s in floating point,
    // which would otherwise display as 00:41:59 instead of 00:42:00.
    const wholeSeconds = Math.round(seconds);
    const hours = Math.floor(wholeSeconds / 3600);
    const minutes = Math.floor((wholeSeconds % 3600) / 60);
    const secs = wholeSeconds % 60;
    
    return {
      value: result.toFixed(2),
      formatted: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`,
      decimal: (seconds / 3600).toFixed(2)
    };
  }, [convertValue, convertFrom, convertTo, mode]);

  const addTimeEntry = () => {
    setTimeEntries([...timeEntries, { id: Date.now(), hours: 0, minutes: 0, seconds: 0 }]);
  };

  const removeTimeEntry = (id) => {
    if (timeEntries.length > 2) {
      setTimeEntries(timeEntries.filter(entry => entry.id !== id));
    }
  };

  const updateTimeEntry = (id, field, value) => {
    const numValue = parseInt(value) || 0;
    setTimeEntries(timeEntries.map(entry => 
      entry.id === id ? { ...entry, [field]: numValue } : entry
    ));
  };

  const updateTime1 = (field, value) => {
    const numValue = parseInt(value) || 0;
    setTime1({ ...time1, [field]: numValue });
  };

  const updateTime2 = (field, value) => {
    const numValue = parseInt(value) || 0;
    setTime2({ ...time2, [field]: numValue });
  };

  const clearAll = () => {
    if (mode === 'somar') {
      setTimeEntries([
        { id: 1, hours: 0, minutes: 0, seconds: 0 },
        { id: 2, hours: 0, minutes: 0, seconds: 0 }
      ]);
    } else if (mode === 'subtrair') {
      setTime1({ hours: 0, minutes: 0, seconds: 0 });
      setTime2({ hours: 0, minutes: 0, seconds: 0 });
    } else {
      setConvertValue('');
    }
  };

  const faqs = [
    {
      question: 'Como usar a calculadora de tempo?',
      answer: 'Digite os valores de horas, minutos e segundos nos campos correspondentes. Escolha se deseja somar ou subtrair. O resultado aparece automaticamente no formato HH:MM:SS e em horas decimais.'
    },
    {
      question: 'Como somar horas, minutos e segundos corretamente?',
      answer: 'A soma de tempo segue o sistema sexagesimal (base 60). Some os segundos primeiro e converta valores acima de 60 em minutos. Repita o mesmo com os minutos. Nossa calculadora faz isso automaticamente para evitar erros.'
    },
    {
      question: 'Qual a diferença entre tempo decimal e sexagesimal?',
      answer: 'No sistema sexagesimal, 1h30min é representado como 1:30. No decimal, o mesmo valor é 1,50 horas. A confusão acontece porque 30 minutos não são 0,30 horas, e sim 0,50. A calculadora converte entre os dois formatos.'
    },
    {
      question: 'Posso somar mais de dois valores de tempo?',
      answer: 'Sim. Clique em "Adicionar linha" para incluir quantas durações precisar. A calculadora soma todas elas de uma vez e mostra o total.'
    },
    {
      question: 'A calculadora funciona no celular?',
      answer: 'Sim. A ferramenta é responsiva e funciona em qualquer dispositivo — celular, tablet ou computador — diretamente no navegador, sem necessidade de instalar aplicativos.'
    },
    {
      question: 'Como converter minutos em horas?',
      answer: 'Divida o total de minutos por 60. O resultado inteiro são as horas e o resto são os minutos. Por exemplo, 150 minutos ÷ 60 = 2 horas e 30 minutos. No modo "Converter" da calculadora, basta digitar o valor e selecionar as unidades.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Calculadora de Tempo: Horas, Minutos e Segundos</title>
        <meta name="description" content="Use a calculadora de tempo online para somar, subtrair e converter horas, minutos e segundos. Ferramenta grátis, rápida e sem cadastro. Resultado instantâneo." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/calculadora-de-tempo" />
        
        <meta property="og:title" content="Calculadora de Tempo: Horas, Minutos e Segundos" />
        <meta property="og:description" content="Use a calculadora de tempo online para somar, subtrair e converter horas, minutos e segundos. Ferramenta grátis, rápida e sem cadastro." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/calculadora-de-tempo" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Calculadora de Tempo: Horas, Minutos e Segundos" />
        <meta name="twitter:description" content="Use a calculadora de tempo online para somar, subtrair e converter horas, minutos e segundos. Ferramenta grátis, rápida e sem cadastro." />
        
        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/calculadora-de-tempo" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/calculadora-de-tempo" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Calculadora de Tempo","item":"https://calculadoradehoras.net.br/calculadora-de-tempo"}
        ]
      }} />

      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Tempo</span>
        </nav>
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Calculadora de Tempo Online
          </h1>
          <p className="text-lg text-body-text">
            Some, subtraia e converta horas, minutos e segundos com precisão
          </p>
          <p className="text-sm text-body-text mt-2" data-testid="last-updated" suppressHydrationWarning>{`Última atualização: ${getCurrentMonthYear()}`}</p>
        </div>

        {/* Mode Selection */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setMode('somar')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              mode === 'somar'
                ? 'bg-blue-primary text-white'
                : 'bg-white text-body-text border-2 border-border-gray hover:border-blue-primary'
            }`}
          >
            <Plus size={20} />
            Somar
          </button>
          <button
            onClick={() => setMode('subtrair')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              mode === 'subtrair'
                ? 'bg-blue-primary text-white'
                : 'bg-white text-body-text border-2 border-border-gray hover:border-blue-primary'
            }`}
          >
            <ArrowRightLeft size={20} />
            Subtrair
          </button>
          <button
            onClick={() => setMode('converter')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              mode === 'converter'
                ? 'bg-blue-primary text-white'
                : 'bg-white text-body-text border-2 border-border-gray hover:border-blue-primary'
            }`}
          >
            <Calculator size={20} />
            Converter
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          {/* Sum Mode */}
          {mode === 'somar' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 mb-4 text-center text-sm font-medium text-body-text">
                <div>Horas</div>
                <div>Minutos</div>
                <div>Segundos</div>
              </div>
              
              {timeEntries.map((entry, index) => (
                <div key={entry.id} className="flex items-center gap-3">
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <input
                      type="number"
                      min="0"
                      value={entry.hours || ''}
                      onChange={(e) => updateTimeEntry(entry.id, 'hours', e.target.value)}
                      className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-center font-mono text-lg focus:outline-none focus:border-blue-primary"
                      placeholder="0"
                    />
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={entry.minutes || ''}
                      onChange={(e) => updateTimeEntry(entry.id, 'minutes', e.target.value)}
                      className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-center font-mono text-lg focus:outline-none focus:border-blue-primary"
                      placeholder="0"
                    />
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={entry.seconds || ''}
                      onChange={(e) => updateTimeEntry(entry.id, 'seconds', e.target.value)}
                      className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-center font-mono text-lg focus:outline-none focus:border-blue-primary"
                      placeholder="0"
                    />
                  </div>
                  {timeEntries.length > 2 && (
                    <button
                      onClick={() => removeTimeEntry(entry.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Remover linha"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>
              ))}
              
              <button
                onClick={addTimeEntry}
                className="flex items-center gap-2 text-blue-primary hover:text-blue-600 font-medium"
              >
                <Plus size={20} />
                Adicionar linha
              </button>
            </div>
          )}

          {/* Subtract Mode */}
          {mode === 'subtrair' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Tempo 1</label>
                <div className="grid grid-cols-3 gap-3">
                  <input
                    type="number"
                    min="0"
                    value={time1.hours || ''}
                    onChange={(e) => updateTime1('hours', e.target.value)}
                    className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-center font-mono text-lg focus:outline-none focus:border-blue-primary"
                    placeholder="HH"
                  />
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={time1.minutes || ''}
                    onChange={(e) => updateTime1('minutes', e.target.value)}
                    className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-center font-mono text-lg focus:outline-none focus:border-blue-primary"
                    placeholder="MM"
                  />
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={time1.seconds || ''}
                    onChange={(e) => updateTime1('seconds', e.target.value)}
                    className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-center font-mono text-lg focus:outline-none focus:border-blue-primary"
                    placeholder="SS"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Tempo 2</label>
                <div className="grid grid-cols-3 gap-3">
                  <input
                    type="number"
                    min="0"
                    value={time2.hours || ''}
                    onChange={(e) => updateTime2('hours', e.target.value)}
                    className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-center font-mono text-lg focus:outline-none focus:border-blue-primary"
                    placeholder="HH"
                  />
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={time2.minutes || ''}
                    onChange={(e) => updateTime2('minutes', e.target.value)}
                    className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-center font-mono text-lg focus:outline-none focus:border-blue-primary"
                    placeholder="MM"
                  />
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={time2.seconds || ''}
                    onChange={(e) => updateTime2('seconds', e.target.value)}
                    className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-center font-mono text-lg focus:outline-none focus:border-blue-primary"
                    placeholder="SS"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Convert Mode */}
          {mode === 'converter' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Valor</label>
                <input
                  type="number"
                  min="0"
                  value={convertValue}
                  onChange={(e) => setConvertValue(e.target.value)}
                  className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary"
                  placeholder="Digite o valor"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">De</label>
                  <select
                    value={convertFrom}
                    onChange={(e) => setConvertFrom(e.target.value)}
                    className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg focus:outline-none focus:border-blue-primary"
                  >
                    <option value="horas">Horas</option>
                    <option value="minutos">Minutos</option>
                    <option value="segundos">Segundos</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">Para</label>
                  <select
                    value={convertTo}
                    onChange={(e) => setConvertTo(e.target.value)}
                    className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg focus:outline-none focus:border-blue-primary"
                  >
                    <option value="horas">Horas</option>
                    <option value="minutos">Minutos</option>
                    <option value="segundos">Segundos</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-4 mt-6">
            <button
              onClick={clearAll}
              className="flex-1 h-[52px] bg-gray-100 text-body-text rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Limpar
            </button>
          </div>
        </div>

        {/* Result Display */}
        {mode === 'somar' && sumResult && (
          <div className="bg-blue-50 rounded-2xl p-6 md:p-8 mb-8">
            <h2 className="text-xl font-semibold text-dark-text mb-4">Resultado</h2>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-primary mb-2">{sumResult.formatted}</div>
              <div className="text-lg text-body-text">
                {`${formatNumber(sumResult.decimal)} horas decimais`}
              </div>
            </div>
          </div>
        )}

        {mode === 'subtrair' && subtractResult && (
          <div className="bg-blue-50 rounded-2xl p-6 md:p-8 mb-8">
            <h2 className="text-xl font-semibold text-dark-text mb-4">Resultado</h2>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-primary mb-2">{subtractResult.formatted}</div>
              <div className="text-lg text-body-text">
                {`${formatNumber(subtractResult.decimal)} horas decimais`}
              </div>
            </div>
          </div>
        )}

        {mode === 'converter' && convertResult && (
          <div className="bg-blue-50 rounded-2xl p-6 md:p-8 mb-8">
            <h2 className="text-xl font-semibold text-dark-text mb-4">Resultado</h2>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-primary mb-2">{`${formatNumber(convertResult.value)} ${convertTo}`}</div>
              <div className="text-lg text-body-text">
                {`${convertResult.formatted} (${formatNumber(convertResult.decimal)} horas)`}
              </div>
            </div>
          </div>
        )}

        <AdSlot />

        {/* Content Section 1 */}
        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            O Que é a Calculadora de Tempo?
          </h2>
          <p className="text-base text-body-text mb-4">
            A <strong>calculadora de tempo</strong> é uma ferramenta online que permite fazer operações com unidades de tempo. Com ela, você pode somar várias durações, subtrair um período de outro ou converter valores entre horas, minutos e segundos.
          </p>
          <p className="text-base text-body-text mb-4">
            Diferente de uma calculadora comum, essa ferramenta usa o <strong>sistema sexagesimal</strong> — onde cada hora possui 60 minutos e cada minuto possui 60 segundos. Isso significa que somar 45 minutos com 30 minutos resulta em 1 hora e 15 minutos, e não 75 minutos. A calculadora faz essa conversão de forma automática para você.
          </p>
          <p className="text-base text-body-text">
            Essa ferramenta é útil tanto para profissionais que precisam controlar horas trabalhadas quanto para estudantes, editores de vídeo, atletas e qualquer pessoa que precise calcular durações com precisão.
          </p>
        </section>

        {/* Content Section 2 */}
        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Como Usar a Calculadora de Tempo
          </h2>
          <p className="text-base text-body-text mb-6">
            O funcionamento é simples e direto. Veja o passo a passo para cada operação disponível.
          </p>

          <h3 className="text-xl font-semibold text-dark-text mb-3">Somar tempos</h3>
          <p className="text-base text-body-text mb-4">
            Para somar duas ou mais durações, preencha os campos de horas, minutos e segundos em cada linha. Clique em "Adicionar linha" se precisar somar mais de dois valores. O resultado aparece automaticamente no formato HH:MM:SS, junto com o valor em horas decimais.
          </p>
          <p className="text-base text-body-text mb-6">
            <strong>Exemplo prático:</strong> Você trabalhou 3h45min na segunda e 4h20min na terça. Insira os valores nas duas linhas e o resultado será <strong>8h05min</strong> (ou 8,08 horas decimais).
          </p>

          <h3 className="text-xl font-semibold text-dark-text mb-3">Subtrair tempos</h3>
          <p className="text-base text-body-text mb-6">
            Selecione o modo "Subtrair". Insira o tempo maior na primeira linha e o tempo menor na segunda. O resultado mostrará a diferença entre os dois períodos. Isso é útil para calcular quanto tempo falta para completar uma jornada, por exemplo.
          </p>

          <h3 className="text-xl font-semibold text-dark-text mb-3">Converter unidades de tempo</h3>
          <p className="text-base text-body-text mb-4">
            No modo "Converter", escolha a unidade de origem e a unidade de destino. Digite o valor e a ferramenta converte automaticamente entre horas, minutos e segundos, com resultado preciso.
          </p>
          <p className="text-base text-body-text">
            <strong>Exemplo:</strong> 150 minutos equivalem a <strong>2 horas e 30 minutos</strong> (2,50 horas decimais).
          </p>
        </section>

        {/* Conversion Table */}
        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Tabela de Conversão de Tempo
          </h2>
          <p className="text-base text-body-text mb-6">
            Consulte os valores mais usados para converter entre horas, minutos e segundos sem precisar de calculadora.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Horas</th>
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Minutos</th>
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Segundos</th>
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Decimal</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="border border-border-gray px-4 py-3">0h15min</td><td className="border border-border-gray px-4 py-3">15 min</td><td className="border border-border-gray px-4 py-3">900 s</td><td className="border border-border-gray px-4 py-3">0,25 h</td></tr>
                <tr className="bg-gray-50"><td className="border border-border-gray px-4 py-3">0h30min</td><td className="border border-border-gray px-4 py-3">30 min</td><td className="border border-border-gray px-4 py-3">1.800 s</td><td className="border border-border-gray px-4 py-3">0,50 h</td></tr>
                <tr><td className="border border-border-gray px-4 py-3">0h45min</td><td className="border border-border-gray px-4 py-3">45 min</td><td className="border border-border-gray px-4 py-3">2.700 s</td><td className="border border-border-gray px-4 py-3">0,75 h</td></tr>
                <tr className="bg-gray-50"><td className="border border-border-gray px-4 py-3">1h00min</td><td className="border border-border-gray px-4 py-3">60 min</td><td className="border border-border-gray px-4 py-3">3.600 s</td><td className="border border-border-gray px-4 py-3">1,00 h</td></tr>
                <tr><td className="border border-border-gray px-4 py-3">1h30min</td><td className="border border-border-gray px-4 py-3">90 min</td><td className="border border-border-gray px-4 py-3">5.400 s</td><td className="border border-border-gray px-4 py-3">1,50 h</td></tr>
                <tr className="bg-gray-50"><td className="border border-border-gray px-4 py-3">2h00min</td><td className="border border-border-gray px-4 py-3">120 min</td><td className="border border-border-gray px-4 py-3">7.200 s</td><td className="border border-border-gray px-4 py-3">2,00 h</td></tr>
                <tr><td className="border border-border-gray px-4 py-3">8h00min</td><td className="border border-border-gray px-4 py-3">480 min</td><td className="border border-border-gray px-4 py-3">28.800 s</td><td className="border border-border-gray px-4 py-3">8,00 h</td></tr>
                <tr className="bg-gray-50"><td className="border border-border-gray px-4 py-3">8h48min</td><td className="border border-border-gray px-4 py-3">528 min</td><td className="border border-border-gray px-4 py-3">31.680 s</td><td className="border border-border-gray px-4 py-3">8,80 h</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Para Que Serve a Calculadora de Tempo?
          </h2>
          <p className="text-base text-body-text mb-6">
            Essa ferramenta tem diversas aplicações no dia a dia. Veja as situações mais comuns onde calcular tempo de forma precisa faz a diferença.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="text-blue-primary flex-shrink-0">
                <Briefcase size={32} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-text mb-2">📋 Controle de Jornada</h3>
                <p className="text-base text-body-text">
                  Some as horas trabalhadas na semana para conferir se bateu as 44 horas da CLT ou se tem horas extras a receber.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-blue-primary flex-shrink-0">
                <Video size={32} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-text mb-2">🎬 Edição de Vídeo</h3>
                <p className="text-base text-body-text">
                  Calcule a duração total do projeto somando a duração de cada clipe na timeline.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-blue-primary flex-shrink-0">
                <BookOpen size={32} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-text mb-2">📚 Estudos</h3>
                <p className="text-base text-body-text">
                  Acompanhe o tempo dedicado a cada matéria e controle sua carga horária de cursos e certificações.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-blue-primary flex-shrink-0">
                <Activity size={32} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-text mb-2">🏃 Esportes</h3>
                <p className="text-base text-body-text">
                  Some tempos de volta em corridas, natação ou ciclismo para acompanhar seu desempenho total.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-blue-primary flex-shrink-0">
                <Truck size={32} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-text mb-2">🚚 Logística</h3>
                <p className="text-base text-body-text">
                  Calcule o tempo total de uma rota somando os trechos de viagem e as paradas intermediárias.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-blue-primary flex-shrink-0">
                <PiggyBank size={32} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-text mb-2">🏦 Banco de Horas</h3>
                <p className="text-base text-body-text">
                  Confira se o saldo do banco de horas está positivo ou negativo com soma e subtração rápidas.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Manual Calculation Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Como Somar Horas Manualmente
          </h2>
          <p className="text-base text-body-text mb-4">
            Entender a lógica por trás do cálculo é importante. Quando você soma tempos à mão, o processo segue estas etapas.
          </p>
          <p className="text-base text-body-text mb-4">
            Primeiro, some os segundos. Se o resultado for 60 ou mais, divida por 60. O quociente vira minuto extra e o resto fica como segundos. Depois, repita o mesmo com os minutos. Se passarem de 60, converta o excedente em horas. Por fim, some as horas normalmente.
          </p>
          <div className="bg-gray-50 rounded-lg p-6 mb-4">
            <p className="text-base text-body-text font-semibold mb-2">
              <strong>Exemplo manual:</strong> Somar 2h47min35s + 1h38min50s
            </p>
            <p className="text-base text-body-text mb-2">Segundos: 35 + 50 = 85 → 1 minuto e 25 segundos</p>
            <p className="text-base text-body-text mb-2">Minutos: 47 + 38 + 1 = 86 → 1 hora e 26 minutos</p>
            <p className="text-base text-body-text mb-2">Horas: 2 + 1 + 1 = 4</p>
            <p className="text-base text-body-text font-semibold mt-4">
              <strong>Resultado: 4h26min25s</strong>
            </p>
          </div>
          <p className="text-base text-body-text">
            A <strong>calculadora de tempo</strong> automatiza todo esse processo. Basta inserir os valores e o resultado aparece em milésimos de segundo, sem risco de erro.
          </p>
        </section>

        {/* Decimal vs Sexagesimal Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Diferença Entre Tempo Sexagesimal e Decimal
          </h2>
          <p className="text-base text-body-text mb-4">
            Uma dúvida comum é a diferença entre os dois formatos de representação do tempo. No <strong>formato sexagesimal</strong> (HH:MM:SS), os minutos vão de 0 a 59 e os segundos também. É o formato usado em relógios e folhas de ponto.
          </p>
          <p className="text-base text-body-text mb-4">
            Já no <strong>formato decimal</strong>, a parte fracionária representa uma fração da hora. Por exemplo, 30 minutos correspondem a 0,50 horas (não 0,30). Essa distinção é crucial em cálculos trabalhistas, porque planilhas de RH costumam usar horas decimais.
          </p>
          <p className="text-base text-body-text">
            Nossa calculadora apresenta o resultado nos dois formatos para que você use o que for mais adequado à sua necessidade.
          </p>
        </section>

        <FAQAccordion faqs={faqs} />

        <RelatedTools 
          tools={[
            { 
              title: 'Calculadora de Horas',
              description: 'Somar e subtrair horas de trabalho',
              link: '/'
            },
            { 
              title: 'Subtração de Horas',
              description: 'Calcule a diferença entre dois horários',
              link: '/calculadora-de-subtracao-de-horas'
            },
            { 
              title: 'Calculadora de Horas Extras',
              description: 'Calcule o valor das suas horas extras',
              link: '/calculadora-de-horas-extras'
            },
            { 
              title: 'Adicional Noturno',
              description: 'Calcule o valor do adicional noturno',
              link: '/calculadora-de-adicional-noturno'
            }
          ]}
        />
      </main>

      <Footer />
    </>
  );
};

export default CalculadoraTempoPage;
